// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import { Field, SubmissionError } from 'redux-form';
import { SNJ_SECONDARY_FILL_WHITE } from '@tcp/core/src/constants/brandSpecific.constants';
import Grid from '@tcp/core/src/components/common/molecules/Grid';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import emailSignupDynamicStore from '@tcp/core/src/components/common/organisms/EmailSignupForm/container/emailSignupDynamicStore';
import smsSignupDynamicStore from '@tcp/core/src/components/common/organisms/SmsSignupForm/container/smsSignupDynamicStore';
import TextBox from '@tcp/core/src/components/common/atoms/TextBox';
import Button from '@tcp/core/src/components/common/atoms/Button';
import Col from '@tcp/core/src/components/common/atoms/Col';
import Row from '@tcp/core/src/components/common/atoms/Row';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import { getBrandInCaps } from '@tcp/core/src/utils/utils';
import {
  GLOBAL_EMAIL_SIGN_UP_LABELS,
  GlobalEmailSignUpLabels,
} from '@tcp/core/src/utils/labels/globalEmailSignUp.labels';
import {
  GLOBAL_SMS_SIGN_UP_LABELS,
  GlobalSmsSignUpLabels,
} from '@tcp/core/src/utils/labels/globalSmsSignUp.labels';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
  getDynamicLabelValues,
} from '@tcp/core/src/utils/labels/labelUtilty';
import style from '../../Footer.style';

const emailFormName = 'FooterTopEmailSignUpForm';
const smsFormName = 'FooterTopSmsSignUpForm';

const SMS_SUBSCRIPTION_STATUS = {
  PENDING: 'PENDING',
  MDN_ALREADY_SUBSCRIBED: 'MdnAlreadySubscribed',
  MDN_CONFIRMATION_BLOCK: 'MdnHasConfirmationBlock',
};

class FooterTopSignUpForm extends React.PureComponent {
  emailSignUpLabels = getLabelFromCopyOrFromTranslation(
    GLOBAL_EMAIL_SIGN_UP_LABELS,
    GlobalEmailSignUpLabels
  );

  smsSignUpLabels = getLabelFromCopyOrFromTranslation(
    GLOBAL_SMS_SIGN_UP_LABELS,
    GlobalSmsSignUpLabels
  );

  formLabels = {
    [emailFormName]: {
      staticLabels: this.emailSignUpLabels,
      dynamicLabels: 'global.emailSignup',
    },
    [smsFormName]: {
      staticLabels: this.smsSignUpLabels,
      dynamicLabels: 'global.smsSignup',
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      isFieldEmpty: true,
    };
    this.fieldValue = '';
  }

  componentDidUpdate({ submitSucceeded: oldSubmitSucceeded }) {
    const { subscription, submitSucceeded, openSuccessModal, fieldName } = this.props;
    if ((subscription.error || subscription.success) && this.formSubmitPromise) {
      if (subscription.error) {
        this.formSubmitPromise.reject();
      } else {
        if (subscription?.data?.status === SMS_SUBSCRIPTION_STATUS.MDN_ALREADY_SUBSCRIBED) {
          this.formSubmitPromise.reject(SMS_SUBSCRIPTION_STATUS.MDN_ALREADY_SUBSCRIBED);
        } else if (subscription?.data?.status === SMS_SUBSCRIPTION_STATUS.MDN_CONFIRMATION_BLOCK) {
          this.formSubmitPromise.reject(SMS_SUBSCRIPTION_STATUS.MDN_CONFIRMATION_BLOCK);
        }
        this.formSubmitPromise.resolve();
      }
      this.formSubmitPromise = null;
    }
    if (
      oldSubmitSucceeded !== submitSucceeded &&
      submitSucceeded &&
      ((fieldName === 'footerTopSmsSignup' &&
        subscription?.data?.status === SMS_SUBSCRIPTION_STATUS.PENDING) ||
        fieldName === 'signup')
    ) {
      this.pushAdobeData(fieldName);
      openSuccessModal(true);
      this.cleanUpForm();
    }
  }

  getFormBasedLabelValue = (formName, labelKey) => {
    const { staticLabels } = this.formLabels[formName] || {};

    return renderLabel(staticLabels[labelKey], labelKey);
  };

  pushAdobeData = (fieldName) => {
    if (fieldName === 'footerTopSmsSignup') {
      pushEventToAdobeAnalytics('phone_subscription');
    } else {
      pushEventToAdobeAnalytics('email_subscription');
    }
  };

  onSignUpInputKeyPress = (e) => {
    if (e.keyCode === 13 || e.which === 13) {
      e.preventDefault();
      this.submitForm();
    }
  };

  cleanUpForm = () => {
    this.setState({ isFieldEmpty: true });
    const { reset } = this.props;
    reset();
  };

  submitForm = async (values) => {
    const { signup, footerTopSmsSignup } = values || {};
    if (footerTopSmsSignup) {
      await smsSignupDynamicStore();
    } else {
      await emailSignupDynamicStore();
    }

    const { onFormSubmit, validateForm, fieldName, form: formName } = this.props;

    const genericErrorText = this.getFormBasedLabelValue(formName, 'lbl_SignUp_genericError');
    const genericErrorLabel = genericErrorText === 'lbl_SignUp_genericError' && '';
    const validationErrorLabel =
      formName === emailFormName
        ? getDynamicLabelValues(`dynamic.global_d.lbl_email_validationErrorLabel`)
        : getDynamicLabelValues(`dynamic.global_d.lbl_sms_validationErrorLabel`);

    // Checking the entered email is not same as previous try
    if (signup && this.fieldValue === signup) {
      const error = { [fieldName]: validationErrorLabel };
      throw new SubmissionError({ ...error, _error: error });
    }

    return validateForm(values)
      .then((subscription) => {
        this.fieldValue = signup;
        if (subscription.error) {
          return Promise.reject();
        }
        return new Promise((resolve, reject) => {
          this.formSubmitPromise = { resolve, reject };
          onFormSubmit({ ...values, component: 'footer' }, subscription?.email?.status);
        });
      })
      .catch((err) => {
        let error = { [fieldName]: genericErrorLabel || validationErrorLabel };
        if (err === SMS_SUBSCRIPTION_STATUS.MDN_ALREADY_SUBSCRIBED) {
          error = { [fieldName]: validationErrorLabel };
        }
        throw new SubmissionError({ ...error, _error: error });
      });
  };

  fieldChange = (element) => {
    const isFieldEmpty = !element.currentTarget.value.trim();
    this.setState({ isFieldEmpty });
  };

  customHandleSubmit = (e) => {
    e.preventDefault();
    setTimeout(() => {
      const { handleSubmit } = this.props;
      handleSubmit(this.submitForm)();
    }, 0);
  };

  render() {
    const {
      pristine,
      submitting,
      dataLocators,
      fieldName,
      fieldProps,
      form: formName,
      isFooterRedesignEnabled,
    } = this.props;
    const { isFieldEmpty } = this.state;
    const placeholderText = this.getFormBasedLabelValue(formName, 'lbl_SignUp_placeholderText');
    const submitButtonText = this.getFormBasedLabelValue(formName, 'lbl_SignUp_submitButtonLabel');

    return (
      <form className="footer_top__signup_form" onSubmit={this.customHandleSubmit}>
        <Grid>
          <Row fullBleed className="footer_top__signup_form_row">
            <Col
              className=""
              colSize={{
                large: 8,
                medium: 4,
                small: 4,
              }}
              ignoreGutter={{
                small: false,
              }}
            >
              <Field
                placeholder={placeholderText}
                name={fieldName}
                id={fieldName}
                type="text"
                component={TextBox}
                dataLocator={dataLocators.inputField}
                errorDataLocator={dataLocators.errorDataLocator}
                enableSuccessCheck={false}
                {...fieldProps}
                isFooterRedesignEnabled={isFooterRedesignEnabled}
                onChange={this.fieldChange}
              />
            </Col>
            <Col
              colSize={{
                large: 4,
                medium: 4,
                small: 2,
              }}
              ignoreGutter={{
                small: true,
              }}
              className="candidate_a_inline_container_button"
            >
              <Button
                disabled={isFieldEmpty || pristine || submitting}
                buttonVariation="fixed-width"
                fill={SNJ_SECONDARY_FILL_WHITE[`${getBrandInCaps()}`]}
                type="submit"
                data-locator={dataLocators.submitButton}
                className="candidate_a_form_button"
              >
                {isFooterRedesignEnabled ? submitButtonText?.toLowerCase() : submitButtonText}
              </Button>
            </Col>
          </Row>
        </Grid>
      </form>
    );
  }
}

FooterTopSignUpForm.propTypes = {
  dataLocators: PropTypes.shape({
    submitButton: PropTypes.string,
    inputField: PropTypes.string,
    checkBoxBrand: PropTypes.string,
    errorDataLocator: PropTypes.string,
  }),
  pristine: PropTypes.bool,
  invalid: PropTypes.bool,
  validateForm: PropTypes.func,
  submitSucceeded: PropTypes.bool,
  submitting: PropTypes.bool,
  subscription: PropTypes.objectOf(PropTypes.shape({})),
  handleSubmit: PropTypes.func,
  onFormSubmit: PropTypes.func,
  openSuccessModal: PropTypes.func,
  reset: PropTypes.func,
  fieldName: PropTypes.string,
  fieldProps: PropTypes.shape({}),
  secondFieldName: PropTypes.string,
  isNavigationFooter: PropTypes.bool.isRequired,
  form: PropTypes.string.isRequired,
};

FooterTopSignUpForm.defaultProps = {
  dataLocators: {
    submitButton: 'email_submit_btn',
    inputField: 'enter_email_text_field',
    checkBox_gym: '',
    checkBox_tcp: '',
  },
  pristine: false,
  invalid: false,
  validateForm: () => {},
  submitSucceeded: false,
  submitting: false,
  subscription: {},
  handleSubmit: () => {},
  onFormSubmit: () => {},
  openSuccessModal: () => {},
  reset: () => {},
  fieldName: 'signup',
  fieldProps: {},
  secondFieldName: '',
};

export default withStyles(FooterTopSignUpForm, style);

export { FooterTopSignUpForm as FooterTopSignUpFormVanilla };
